import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMoney } from "@/lib/currency";
import { queryOptions, useSuspenseQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { normalizeLang } from "@/i18n";
import { getProduct, getAvailability, getPriceList } from "@/lib/bokun.functions";
import { addToCart } from "@/lib/cart.functions";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ProductGalleryLazy as ProductGallery } from "@/components/product/ProductGalleryLazy";

/**
 * Renders third-party HTML (e.g. Bokun product descriptions) after
 * sanitizing with DOMPurify on the client. We never inject the raw HTML
 * during SSR — if Bokun were ever compromised, an `<script>` payload would
 * otherwise execute for every visitor.
 */
function SafeBokunHtml({ html }: { html: string }) {
  const [clean, setClean] = useState<string | null>(null);
  useEffect(() => {
    let mounted = true;
    void import("dompurify").then(({ default: DOMPurify }) => {
      if (!mounted) return;
      setClean(
        DOMPurify.sanitize(html, {
          ALLOWED_TAGS: [
            "p", "br", "hr", "ul", "ol", "li",
            "b", "strong", "i", "em", "u", "a", "span",
            "h2", "h3", "h4", "blockquote",
          ],
          ALLOWED_ATTR: ["href", "target", "rel"],
          ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|#)/i,
        }),
      );
    });
    return () => {
      mounted = false;
    };
  }, [html]);
  if (clean === null) return null;
  return (
    <div
      className="prose prose-sm max-w-none text-sm text-muted-foreground"
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}

const productQuery = (id: string, lang: string) =>
  queryOptions({
    queryKey: ["bokun", "product", id, lang],
    queryFn: () => getProduct({ data: { id, lang } }),
    staleTime: 5 * 60_000,
  });

const availabilityQuery = (id: string) =>
  queryOptions({
    queryKey: ["bokun", "availability", id],
    queryFn: () => getAvailability({ data: { id } }),
    staleTime: 60_000,
  });

const priceListQuery = (id: string) =>
  queryOptions({
    queryKey: ["bokun", "price-list", id],
    queryFn: () => getPriceList({ data: { id } }),
    // Bokun pricing is authoritative; refresh aggressively to stay in sync.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });

export const Route = createFileRoute("/products/$id")({
  head: ({ params }) => ({
    meta: [{ title: `Producto ${params.id} — Mostrando México` }],
  }),
  loader: ({ context, params }) => {
    context.queryClient.ensureQueryData(productQuery(params.id, context.lang));
    context.queryClient.prefetchQuery(availabilityQuery(params.id));
    context.queryClient.prefetchQuery(priceListQuery(params.id));
  },
  component: ProductPage,
  errorComponent: ({ error }) => (
    <div className="mx-auto max-w-2xl p-8 text-center">
      <h1 className="text-xl font-semibold">No se pudo cargar el producto</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
    </div>
  ),
  notFoundComponent: () => <div className="p-8 text-center">Producto no encontrado.</div>,
});

function ProductPage() {
  const { id } = Route.useParams();
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
            ← Volver al catálogo
          </Link>
          <Link to="/cart" className="text-sm font-medium hover:underline">Carrito</Link>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-8">
        <Suspense fallback={<Skeleton className="h-96 w-full" />}>
          <ProductDetail id={id} />
        </Suspense>
      </main>
    </div>
  );
}

function ProductDetail({ id }: { id: string }) {
  const money = useMoney();
  const { i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const { data } = useSuspenseQuery(productQuery(id, lang));
  const p = data.product;
  return (
    <article className="space-y-8">
      <div className="grid gap-6 md:grid-cols-2">
        <ProductGallery images={p.images} alt={p.title} />
        <div className="space-y-4">
          <h1 className="text-3xl font-semibold tracking-tight">{p.title}</h1>
          {p.location && <p className="text-sm text-muted-foreground">{p.location}</p>}
          <div className="flex flex-wrap gap-2">
            {p.categories.map((c) => (
              <Badge key={c} variant="secondary">{c}</Badge>
            ))}
          </div>
          <div className="rounded-md border p-4">
            <div className="text-2xl font-semibold">
              {p.priceFrom !== null ? `Desde ${money(p.priceFrom, p.currency)}` : "Precio bajo solicitud"}
            </div>
            {p.durationMinutes !== null && (
              <div className="text-sm text-muted-foreground">
                Duración: {Math.round((p.durationMinutes / 60) * 10) / 10} horas
              </div>
            )}
          </div>
        </div>
      </div>

      {p.description && (
        <section>
          <h2 className="mb-2 text-lg font-semibold">Descripción</h2>
          <SafeBokunHtml html={p.description} />
        </section>
      )}

      <section className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold">Reserva tu actividad</h2>
        <Suspense fallback={<Skeleton className="h-48 w-full" />}>
          <BookingSelector id={id} />
        </Suspense>
      </section>
    </article>
  );
}

function BookingSelector({ id }: { id: string }) {
  const money = useMoney();
  const { i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const { data: prodData } = useSuspenseQuery(productQuery(id, lang));
  const { data: availData } = useSuspenseQuery(availabilityQuery(id));
  const { data: priceData } = useSuspenseQuery(priceListQuery(id));
  const p = prodData.product;
  const days = availData.days.filter((d) => d.available);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [selectedDate, setSelectedDate] = useState<string>(days[0]?.date ?? "");
  const day = days.find((d) => d.date === selectedDate) ?? null;

  // Default categories: if Bokun didn't return them, fall back to "Adult".
  const categories = p.pricingCategories.length
    ? p.pricingCategories
    : [{ id: "default", title: "Adulto", minAge: null, maxAge: null, ticketCategory: null }];

  // Real-time price-per-category map from Bokun's price list.
  const priceByCategory = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of priceData.entries ?? []) {
      if (e.price != null) m.set(e.pricingCategoryId, e.price);
    }
    return m;
  }, [priceData]);

  const fallbackUnit = day?.price ?? p.priceFrom ?? 0;

  const unitPriceFor = (catId: string): number =>
    priceByCategory.get(catId) ?? fallbackUnit;

  const [qty, setQty] = useState<Record<string, number>>(() =>
    Object.fromEntries(categories.map((c, i) => [c.id, i === 0 ? 1 : 0])),
  );

  const total = useMemo(
    () =>
      categories.reduce(
        (s, c) => s + unitPriceFor(c.id) * (qty[c.id] ?? 0),
        0,
      ),
    [qty, categories, priceByCategory, fallbackUnit],
  );

  const totalPax = Object.values(qty).reduce((s, n) => s + n, 0);
  const currency = day?.currency ?? p.currency ?? "USD";

  const mut = useMutation({
    mutationFn: () =>
      addToCart({
        data: {
          productId: p.id,
          title: p.title,
          image: p.images[0] ?? null,
          date: selectedDate,
          startTimeId: day?.startTimeId ?? null,
          startTime: day?.startTime ?? null,
          currency,
          pricingCategoryBookings: categories.map((c) => ({
            pricingCategoryId: c.id,
            title: c.title,
            quantity: qty[c.id] ?? 0,
            unitPrice: priceByCategory.get(c.id) ?? day?.price ?? p.priceFrom ?? null,
          })),
          extras: [],
          pickupPlaceId: null,
          unitTotal: total,
        },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["cart"] });
      navigate({ to: "/checkout" });
    },
  });

  if (!days.length) {
    return <p className="text-sm text-muted-foreground">Sin disponibilidad en los próximos 30 días.</p>;
  }

  return (
    <div className="space-y-4">
      <label className="block text-sm">
        <span className="mb-1 block font-medium">Fecha</span>
        <select
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="w-full rounded-md border bg-background p-2"
        >
          {days.map((d) => (
            <option key={d.date + (d.startTimeId ?? "")} value={d.date}>
              {d.date}
              {d.startTime ? ` · ${d.startTime}` : ""}
              {d.price !== null ? ` · ${money(d.price, d.currency)}` : ""}
            </option>
          ))}
        </select>
      </label>

      <div className="space-y-2">
        {categories.map((c) => {
          const unit = unitPriceFor(c.id);
          const live = priceByCategory.has(c.id);
          return (
            <div key={c.id} className="flex items-center justify-between rounded-md border p-3">
              <div>
                <div className="text-sm font-medium">{c.title}</div>
                {(c.minAge !== null || c.maxAge !== null) && (
                  <div className="text-xs text-muted-foreground">
                    Edad {c.minAge ?? "?"}–{c.maxAge ?? "?"}
                  </div>
                )}
                <div className="text-xs text-muted-foreground">
                  {money(unit, currency)}{!live ? " · precio estimado" : ""}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setQty((q) => ({ ...q, [c.id]: Math.max(0, (q[c.id] ?? 0) - 1) }))}
                >
                  −
                </Button>
                <span className="w-8 text-center text-sm">{qty[c.id] ?? 0}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setQty((q) => ({ ...q, [c.id]: Math.min(50, (q[c.id] ?? 0) + 1) }))}
                >
                  +
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between rounded-md bg-muted/40 p-3 text-sm">
        <span>Total ({totalPax} pax)</span>
        <span className="font-semibold">
          {money(total, currency)}
        </span>
      </div>

      {mut.error && (
        <p className="text-sm text-destructive">
          {(mut.error as Error).message ?? "No se pudo añadir al carrito."}
        </p>
      )}

      <Button
        className="w-full"
        disabled={!selectedDate || totalPax === 0 || mut.isPending}
        onClick={() => mut.mutate()}
      >
        {mut.isPending ? "Añadiendo…" : "Añadir al carrito y continuar"}
      </Button>
    </div>
  );
}
